import {t} from '@lingui/macro';
import {DateTime} from 'luxon';
import React, {Component, createRef, RefObject} from 'react';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {BoxedInput} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {BoxedInputChildProps} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {
  InternationalizationContextProps,
  withInternationalization,
} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {checkTimeIntervalOverlap, StartEndData} from '../../../../helpers/calendarHelpers';
import {buildCompleteMeetingRoomName} from '../../../../helpers/models/meetingRoomHelpers';
import {ChannelModel} from '../../../../models/api/channelModel';
import {CompanyModel} from '../../../../models/api/companyModel';
import {MeetingRoomModel} from '../../../../models/api/meetingRoomModel';
import {fetchMeetingRoomsAsync} from '../../../../services/calendar/meetingRooms';
import {MeetingRoomsDropdown} from './meetingRoomsDropdown';

/*
 * Props.
 */

interface MeetingRoomsDropdownBoxProps extends InternationalizationContextProps {
  company: CompanyModel;
  roomAddressesToExclude: ReadonlyArray<string>;
  startEndData: StartEndData;
  channel: ChannelModel;
  onMeetingRoomSelect: HandlerOf<MeetingRoomModel>;
  onGoToReAuthChannelSettingsClick: Handler;
  className?: string;
}

/*
 * Component.
 */

interface MeetingRoomParameters {
  rangeStartDateTime: DateTime;
  rangeEndDateTime: DateTime;
  channelId: number;
}

interface MeetingRoomsDropdownBoxState {
  value: string;
  isInputFocused: boolean;
  shouldIncludeUnavailableRooms: boolean;
  parameters: MeetingRoomParameters | undefined;
  meetingRooms: ReadonlyArray<MeetingRoomModel>;
  status: AsyncStatusesEnum;
  insufficientScopesChannelId: number | undefined;
}

class MeetingRoomsDropdownBoxComponent extends Component<
  MeetingRoomsDropdownBoxProps,
  MeetingRoomsDropdownBoxState
> {
  constructor(props: MeetingRoomsDropdownBoxProps) {
    super(props);
    this.state = {
      value: '',
      isInputFocused: false,
      shouldIncludeUnavailableRooms: false,
      meetingRooms: [],
      parameters: undefined,
      status: AsyncStatusesEnum.LOADED,
      insufficientScopesChannelId: undefined,
    };

    this.inputRef = createRef<BoxedInput>();
    this.boxRef = createRef<HTMLDivElement>();
    this.hasUnmounted = false;
  }

  private readonly inputRef: RefObject<BoxedInput>;
  private readonly boxRef: RefObject<HTMLDivElement>;
  private hasUnmounted: boolean;

  componentDidUpdate() {
    if (this.state.isInputFocused) {
      this.maybeFetchMeetingRooms();
    }
  }

  componentWillUnmount() {
    this.hasUnmounted = true;
  }

  private readonly maybeFetchMeetingRooms = async (shouldForceFetch?: boolean) => {
    const rangeStartDateTime = this.props.startEndData.startDateTime.startOf('day');
    const rangeEndDateTime = this.props.startEndData.endDateTime.endOf('day');
    const {parameters} = this.state;

    // Refetch meeting rooms if the freebusy range we're examining or the channelId has changed.
    const shouldFetch =
      !parameters ||
      !rangeStartDateTime.equals(parameters.rangeStartDateTime) ||
      !rangeEndDateTime.equals(parameters.rangeEndDateTime) ||
      this.props.channel.id !== parameters.channelId;

    if (!shouldFetch && !shouldForceFetch) {
      return;
    }

    this.setState({
      parameters: {
        rangeStartDateTime,
        rangeEndDateTime,
        channelId: this.props.channel.id,
      },
      status: AsyncStatusesEnum.LOADING,
    });

    try {
      const meetingRooms = await fetchMeetingRoomsAsync(
        this.props.company.url,
        this.props.channel.id,
        rangeStartDateTime,
        rangeEndDateTime,
      );

      if (this.hasUnmounted) {
        return;
      }

      this.setState({meetingRooms, status: AsyncStatusesEnum.LOADED, insufficientScopesChannelId: undefined});
    } catch (err) {
      if (this.hasUnmounted) {
        return;
      }

      if (err && err.body && err.body.status === 'insufficient_scopes') {
        this.setState({insufficientScopesChannelId: this.props.channel.id});
      }

      this.setState({status: AsyncStatusesEnum.FAILED});
    }
  };

  private readonly onSelect = (meetingRoom: MeetingRoomModel) => {
    const currentInput = this.inputRef.current;
    if (!currentInput) {
      return;
    }

    currentInput.blur();
    this.setState({value: ''});
    this.props.onMeetingRoomSelect(meetingRoom);
  };

  render() {
    return (
      <>
        <BoxedInput ref={this.inputRef} boxRef={this.boxRef} boxClassName={this.props.className}>
          {(childProps) => this.renderInputContent(childProps)}
        </BoxedInput>
        {this.state.isInputFocused && this.renderDropdown()}
      </>
    );
  }

  private renderInputContent(childProps: BoxedInputChildProps) {
    const {localize} = this.props;
    return (
      <BoxedInputElement
        {...childProps}
        onChange={(value) => this.setState({value})}
        onFocus={() => this.setState({isInputFocused: true})}
        onBlur={() => this.setState({isInputFocused: false})}
        placeholder={localize(t('meeting-room-dropdown-box')`Add meeting room`)}
        type={BoxedInputElementTypesEnum.TEXT}
        value={this.state.value}
      />
    );
  }

  private renderDropdown() {
    const filteredMeetingRooms = this.state.meetingRooms.filter((r) => {
      // If this room is missing freebusy information, assume it's busy.
      if (!r.busyIntervals) {
        return false;
      }

      if (this.props.roomAddressesToExclude.includes(r.address)) {
        return false;
      }

      const includesQuery = buildCompleteMeetingRoomName(r)
        .toLowerCase()
        .includes(this.state.value.toLowerCase());
      if (!includesQuery) {
        return false;
      }

      if (this.state.shouldIncludeUnavailableRooms) {
        return true;
      }

      return !checkTimeIntervalOverlap(this.props.startEndData, r.busyIntervals);
    });

    return (
      <MeetingRoomsDropdown
        {...this.props}
        anchor={this.boxRef}
        meetingRooms={filteredMeetingRooms}
        channel={this.props.channel}
        hasSearchQuery={Boolean(this.state.value)}
        status={this.state.status}
        hasInsufficientScopes={this.state.insufficientScopesChannelId === this.props.channel.id}
        shouldIncludeUnavailableRooms={this.state.shouldIncludeUnavailableRooms}
        setShouldIncludeUnavailableRooms={(shouldIncludeUnavailableRooms) =>
          this.setState({shouldIncludeUnavailableRooms})
        }
        onMeetingRoomClick={this.onSelect}
        onRetryClick={() => this.maybeFetchMeetingRooms(true)}
        onGoToReAuthChannelSettingsClick={() => {
          this.setState({
            isInputFocused: false,
            insufficientScopesChannelId: undefined,
            parameters: undefined,
            status: AsyncStatusesEnum.LOADED,
          });
          this.props.onGoToReAuthChannelSettingsClick();
        }}
        height={305}
        width={270}
      />
    );
  }
}

export const MeetingRoomsDropdownBox = withInternationalization(MeetingRoomsDropdownBoxComponent);
